import { useEffect } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  CheckmarkOutline,
  Information,
  Upload,
  Warning,
} from '@mint-lib/icons';
import { Button, Chip, typographyVariants } from '@mint-lib/tw-components';
import { Alert } from '@mint-lib/tw-components/patterns';

import {
  BackButton,
  DiscardButton,
  Switch,
} from '../../../components/atoms/index.js';
import { RichTooltip } from '../../../components/atoms/RichTooltip.jsx';
import { ErrorsFoundLabel } from '../../../components/molecules/ErrorsFoundLabel.jsx';
import { NavbarSlot } from '../../../components/molecules/NavbarSlot.jsx';
import { ValidationProgressState } from '../../../components/molecules/ValidationProgressState.jsx';
import { DataTable } from '../../../components/organisms/DataTable.jsx';
import { useDataFileContext } from '../../../contexts/DataFileContext/useDataFileContext.js';
import {
  useImportHubV2,
  useNativeMetricsPlatformMediaRow,
} from '../../../hooks/ff/index.js';
import { useDiscard } from '../../../hooks/useDiscard.js';
import { useValidationRouteApi } from '../../../hooks/useImportHubRouteApi.js';
import {
  useDataFileServerStepsConfig,
  useDiscardValidatedRows,
} from '../../../queries/index.js';
import {
  getNativeMetricsBannerDescription,
  getNativeMetricsBannerSeverity,
} from './nativeMetricsBanner.js';
import { useValidatedData } from './useValidatedData.js';

export const ValidationStep = () => {
  const { t } = useTranslation('@myn/import-hub');
  const validationRoute = useValidationRouteApi();
  const navigate = validationRoute.useNavigate();
  const searchParams = validationRoute.useSearch();

  const {
    dataFileId,
    goToNextStep,
    goToPrevStep,
    isSubmitting,
    showUploadFileDialog,
  } = useDataFileContext();

  const { mutateAsync: discardDataFileRows, isPending: isDiscarding } =
    useDiscardValidatedRows(dataFileId, searchParams);

  const {
    selection,
    selectedRowsIds,
    hasSelection,
    resetSelection,
    handleSelectAllRows,
    handleSelectRow,
    discardRows,
  } = useDiscard({
    dataFileId,
    searchParams: searchParams,
    discardDataFileRows,
  });

  const isNativeMetricsFFEnabled = useNativeMetricsPlatformMediaRow();
  const isImportHubV2 = useImportHubV2();

  const {
    isValidating,
    validationState,
    dataFilePreview,
    validatedRows,
    errorsCount,
    nativeMetricsBanner,
    hasImportableData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useValidatedData(dataFileId, searchParams, {
    enabled: !isDiscarding,
  });

  const stepsConfig = useDataFileServerStepsConfig(dataFileId);
  const isLastStep = stepsConfig.isLastStep(stepsConfig.validation.name);

  const headers = validatedRows?.pages[0].additional.headers;
  const rows = validatedRows?.pages.flatMap((page) => page.items) ?? [];
  const hasRows = Boolean(rows.length);
  const hasErrors = Boolean(errorsCount);
  const hasDeactivatedMetrics = Boolean(
    headers?.some((header) => header.isDisabled),
  );

  const isValid = !isLoading && !hasErrors && !dataFilePreview.isEdited;

  useEffect(() => {
    if (isValid && searchParams.onlyErrors) {
      navigate({
        search: (prev) => ({ ...prev, onlyErrors: false }),
      });
    }
  }, [isValid, navigate, searchParams.onlyErrors]);

  const handleShowOnlyRowsWithErrorsChange = (checked: boolean) => {
    resetSelection();
    navigate({
      search: (prev) => ({ ...prev, onlyErrors: checked }),
    });
  };

  const hasNoDataToImport = isNativeMetricsFFEnabled && !hasImportableData;
  const isNextButtonDisabled =
    isLoading ||
    isDiscarding ||
    !hasRows ||
    hasErrors ||
    isSubmitting ||
    hasDeactivatedMetrics ||
    hasNoDataToImport;
  const nextButtonTooltip = hasNoDataToImport
    ? t("You can't proceed because there's no data to import.")
    : t(
        'Edit error-containing rows or delete them before completing the import.',
      );

  const actions = (
    <>
      <Button
        onClick={showUploadFileDialog}
        variant="text"
        disabled={isLoading || isValidating || isSubmitting}
        data-dd-action-name="Replace file"
      >
        <Upload />
        {t('Replace file')}
      </Button>
      <BackButton
        onClick={goToPrevStep}
        disabled={isValidating || isSubmitting}
      />
      <RichTooltip
        asChild={true}
        description={nextButtonTooltip}
        side="left"
        disabled={!hasErrors && hasImportableData}
      >
        <div>
          {!isValid && (
            <Button
              disabled={isNextButtonDisabled}
              onClick={goToNextStep}
              data-dd-action-name="Validate"
            >
              {t('Validate')}
            </Button>
          )}
          {isValid && isLastStep && (
            <Button
              disabled={isNextButtonDisabled}
              onClick={goToNextStep}
              data-dd-action-name="Finish import"
            >
              {t('Finish import')}
            </Button>
          )}
          {isValid && !isLastStep && (
            <Button
              disabled={isNextButtonDisabled}
              onClick={goToNextStep}
              data-dd-action-name="Next"
            >
              {t('Next')}
            </Button>
          )}
        </div>
      </RichTooltip>
    </>
  );

  if (isValidating) {
    return (
      <>
        <NavbarSlot>{actions}</NavbarSlot>
        <ValidationProgressState validationState={validationState} />
      </>
    );
  }

  return (
    <>
      <NavbarSlot>{actions}</NavbarSlot>
      {hasDeactivatedMetrics && (
        <Alert
          severity="error"
          variant="inline"
          title={t('Some metrics have been deactivated.')}
          description={t(
            'To proceed with the import, remove them from the Data mapping step.',
          )}
          removable={false}
          className="mb-6"
        />
      )}
      <div className="mb-6 flex flex-col">
        <div className="mb-2 flex items-center gap-3">
          <h4 className={typographyVariants({ variant: 'h4' })}>
            {t('Data check')}
          </h4>
          {isImportHubV2 && (
            <RichTooltip
              asChild
              className="w-[430px]"
              description={t(
                'Data will be displayed in Reporting following the configured precision. The original value remains stored in DB and can be viewed by adjusting precision settings.',
              )}
            >
              <Chip
                variant="outline"
                color="tertiary"
                size="small"
                className="gap-0.5"
              >
                <Information className="size-4" />
                {t('Decimal precision')}
              </Chip>
            </RichTooltip>
          )}
        </div>
        <p className={typographyVariants({ variant: 'bodyLong01' })}>
          {t(
            'Review and edit cells if necessary. Discard any unwanted rows, and proceed or return to the upload phase.',
          )}
        </p>
      </div>
      {isNativeMetricsFFEnabled && nativeMetricsBanner && (
        <Alert
          severity={getNativeMetricsBannerSeverity(nativeMetricsBanner)}
          variant="inline"
          description={getNativeMetricsBannerDescription(
            nativeMetricsBanner,
            t,
          )}
          removable={false}
          className="mb-4"
        />
      )}
      <div className="mb-4 flex items-center gap-2">
        <DiscardButton
          onDiscard={discardRows}
          disabled={!hasSelection || isDiscarding}
        />
        <div className="ml-auto flex items-center gap-4">
          {hasErrors ? (
            <ErrorsFoundLabel
              className="text-error-dark"
              message={t('Rows with errors found')}
              icon={Warning}
            />
          ) : (
            !dataFilePreview.isEdited && (
              <ErrorsFoundLabel
                className="text-success-dark"
                message={t('No errors found')}
                icon={CheckmarkOutline}
              />
            )
          )}
          {(hasErrors || searchParams.onlyErrors) && (
            <Switch
              data-dd-action-name="Validation: show only errors"
              label={t('Show only errors')}
              checked={Boolean(searchParams.onlyErrors)}
              onCheckedChange={handleShowOnlyRowsWithErrorsChange}
              size="small"
            />
          )}
        </div>
      </div>
      <DataTable
        {...{
          dataFileId,
          searchParams,
          data: validatedRows,
          headers,
          rows,
          selection,
          selectedRowsIds,
          handleSelectAllRows,
          handleSelectRow,
          isLoading,
          isFetchingNextPage,
          hasNextPage,
          fetchNextPage,
        }}
      />
    </>
  );
};
